import React, { ReactNode, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cn from 'clsx';
import { getOperationAST } from 'graphql/utilities';
import styles from './style.module.scss';
import Button from '@components-clean/core/button';
import Card from '@components-clean/core/card';
import CreatedUpdatedInfo from '@components-clean/core/created-updated-info';
import { DatePickerController } from '@components-clean/core/date-picker';
import DragDropListsCore from '@components-clean/core/drag-drop-lists-core';
import DragDropItem from '@components-clean/core/drag-drop-lists-core/drag-drop-item';
import { DragDropListItemProps } from '@components-clean/core/drag-drop-lists-core/drag-drop-lists-core.interface';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import { TextAreaController } from '@components-clean/core/text-area';
import { TextBoxController } from '@components-clean/core/text-box';
import { useUnsavedChangesTracking, useUnsavedChangesConfirmation } from '@hooks-clean/index';
import { Attendee, RiskReview } from '@interfaces/report-templates.interface';
import { IRefreshGridEventBusMessage, REFRESH_GRID } from '@pages-clean/risk-assessment/constants';
import ManageAttendeeDialog from '@pages-clean/risk-assessment/monitoring-toolkit/manage-reviews-tab/manage-attendee-dialog';
import { assumeUtc, getUTCDate } from '@utils-clean/date-time-helpers';
import { busDispatch } from '@utils-clean/eventBus';
import { NullableStudy } from '@contexts/local-state-context';
import { useTranslation, useBoolean } from '@hooks/index';
import { getIsActiveField, javascriptDateWithoutTimeFromUTC, stringDateWithoutTimeFromUtc } from '@utils/convertor';
import { ShowError, ShowSuccess } from '@utils/notification';
import {
    updateControlsHubChartConfig,
    updateControlsHubChartConfigVariables,
} from '@queries/generated/risk-assessment.generated';
import { GET_CM_CONTROLS_HUB_CHART_CONFIG, UPSERT_CM_CONTROLS_HUB_CHART_CONFIG } from '@queries/risk-assessment';
import ControlsHubApi from '@apis/bff-api/central-monitoring/controls-hub';
import { useGetAttendeesList, useGetOpenRiskReviewCount, useMutationRiskReview } from '@apis/bff-api/report-templates';

export interface ManageRiskReviewDialogProps {
    onClose: () => void;
    onSubmit: (riskReviewId: string | undefined) => void;
    studyId: string;
    riskReview?: RiskReview;
    hasOtherOpenRecord: boolean;
    study?: NullableStudy;
}

interface ManageRiskReviewFormInput {
    description: string;
    protocolVersion: string;
    startDate: Date | null;
    endDate: Date | null;
    comments: string;
    attendeesDirty: boolean;
}

interface AttendeeDragDropListItem extends DragDropListItemProps {
    riskReviewAttendeeId: string;
}

const COMPONENT_NAME = 'manage-risk-reviews-dialog';

const mapAttendeesToAttendeesItems = (attendees: Attendee[]): AttendeeDragDropListItem[] =>
    attendees.map((attendee) => ({
        title: `${attendee.firstName} ${attendee.lastName}`,
        subtitle: attendee.jobTitle,
        riskReviewAttendeeId: attendee.id!,
        active: attendee.active,
    }));

const ManageRiskReviewDialog = ({
    riskReview,
    onSubmit,
    onClose,
    studyId,
    hasOtherOpenRecord,
    study,
}: ManageRiskReviewDialogProps) => {
    const { t } = useTranslation();

    const [upsertControlsHubChartConfig] = useMutation<
        updateControlsHubChartConfig,
        updateControlsHubChartConfigVariables
    >(UPSERT_CM_CONTROLS_HUB_CHART_CONFIG);

    const { data: openRiskReviewCountData } = useGetOpenRiskReviewCount(studyId);

    const endDateDisabled = !!riskReview?.endDate && hasOtherOpenRecord;

    let dialogCreatedUpdatedInfo: ReactNode | undefined;
    let dialogPrimaryButtonLabel: string;
    let dialogTitle: string;
    let dialogInitialHeight: number;

    if (riskReview) {
        dialogTitle = t('ra:editRiskReviewId', { id: riskReview._itemId ?? riskReview.recordId });
        dialogPrimaryButtonLabel = t('common:update');
        dialogInitialHeight = 765;
        dialogCreatedUpdatedInfo = (
            <CreatedUpdatedInfo
                created={`${stringDateWithoutTimeFromUtc(riskReview.createdDate, 'DD-MMM-YYYY')} by ${riskReview.createdByName}`}
                updated={`${stringDateWithoutTimeFromUtc(riskReview.updatedDate, 'DD-MMM-YYYY')} by ${riskReview.updatedByName}`}
            />
        );
    } else {
        dialogTitle = t('ra:addRiskReview');
        dialogPrimaryButtonLabel = t('common:add');
        dialogInitialHeight = 715;
    }

    const {
        control,
        handleSubmit,
        setValue,
        watch,
        formState: { isValid, isDirty, isSubmitting },
    } = useForm<ManageRiskReviewFormInput>({
        mode: 'all',
        defaultValues: {
            description: riskReview?.description ?? '',
            protocolVersion: riskReview?.protocolVersion ?? '',
            startDate: riskReview?.startDate ? javascriptDateWithoutTimeFromUTC(riskReview.startDate) : getUTCDate(),
            endDate: riskReview?.endDate ? javascriptDateWithoutTimeFromUTC(riskReview.endDate) : null,
            comments: riskReview?.comments ?? '',
            attendeesDirty: false,
        },
    });

    const startDate = watch('startDate');
    const endDate = watch('endDate');

    const { trigger: putRiskReview } = useMutationRiskReview();
    const { attendeesData, isLoadingAttendees, mutateAttendees } = useGetAttendeesList(studyId);

    const StudyHasCM2 = study && getIsActiveField('CM2', study.modules);
    const { data: latestDataset, isLoading: datasetLoading } = ControlsHubApi.useGetLatestActiveDataSet(
        studyId,
        StudyHasCM2,
    );

    useUnsavedChangesTracking({
        area: 'risk-assessment-toolkit',
        componentName: COMPONENT_NAME,
        hasChanges: isDirty,
    });
    const confirmLossOfChanges = useUnsavedChangesConfirmation({
        area: 'risk-assessment-toolkit',
        mode: 'individual-components',
        componentNames: COMPONENT_NAME,
    });

    const showManageAttendeesDialog = useBoolean(false);
    const [availableAttendeesItems, setAvailableAttendeeItems] = useState<AttendeeDragDropListItem[]>([]);
    const [pendingSelectedAttendeeIds, setPendingSelectedAttendeeIds] = useState<string[]>([]);

    const riskReviewAttendees = riskReview?.attendees ?? [];
    const [selectedAttendeeItems, setSelectedAttendeeItems] = useState<AttendeeDragDropListItem[]>(
        mapAttendeesToAttendeesItems(riskReviewAttendees),
    );

    const hasNewAttendees = () =>
        !selectedAttendeeItems.every((item) =>
            riskReviewAttendees.some((attendee) => item.riskReviewAttendeeId === attendee.id),
        );

    const updateAttendeesDirty = (attendees: Attendee[]) => {
        const attendeesChanged =
            attendees.length !== riskReviewAttendees.length ||
            attendees.some((attendee, index) => riskReviewAttendees[index].id !== attendee.id);

        setValue('attendeesDirty', attendeesChanged, { shouldDirty: true });
    };

    const onAttendeeDialogSubmitted = async (attendeeId: string | undefined) => {
        showManageAttendeesDialog.setFalse();
        if (attendeeId) {
            setPendingSelectedAttendeeIds((prev) => [...prev, attendeeId!]);
            await mutateAttendees();
        }
    };

    const onSubmitHandler = async (manageRiskReviewFormInput: ManageRiskReviewFormInput) => {
        const attendeesSource = hasNewAttendees() ? attendeesData : attendeesData || riskReviewAttendees;

        const attendees = selectedAttendeeItems.map((item, index) => ({
            ...attendeesSource!.find((attendee) => attendee.id === item.riskReviewAttendeeId)!,
            sortOrder: index + 1,
        }));

        const riskReviewPayload: RiskReview = {
            studyId,
            description: manageRiskReviewFormInput.description,
            protocolVersion: manageRiskReviewFormInput.protocolVersion,
            startDate: assumeUtc(manageRiskReviewFormInput.startDate!)!,
            endDate: manageRiskReviewFormInput.endDate ? assumeUtc(manageRiskReviewFormInput.endDate) : null,
            attendees,
            comments: manageRiskReviewFormInput.comments,
            ...(riskReview?.id ? { id: riskReview.id } : {}),
        };

        try {
            const result = await putRiskReview(riskReviewPayload);
            const [id] = result as string[];

            if (
                !riskReview?.id ||
                (!riskReview?.endDate && manageRiskReviewFormInput.endDate) ||
                (riskReview?.endDate && !manageRiskReviewFormInput.endDate)
            ) {
                if (openRiskReviewCountData && openRiskReviewCountData > 1 && manageRiskReviewFormInput.endDate) {
                    ShowSuccess(t('common:actionsSuccessfulToast'));
                    onSubmit(id);
                    return;
                }
                const operationName = getOperationAST(GET_CM_CONTROLS_HUB_CHART_CONFIG)?.name?.value;
                await upsertControlsHubChartConfig({
                    variables: {
                        studyId,
                        ...(manageRiskReviewFormInput.endDate ? {} : { cutoffDate: latestDataset?.dataCutDate! }),
                    },
                    refetchQueries: [operationName!],
                })
                    .then(async (response) => {
                        if (!response?.data || !response?.data?.updateControlsHubChartConfig) {
                            onSubmit('error');
                            return;
                        }
                        if (response && response.data?.updateControlsHubChartConfig) {
                            busDispatch<IRefreshGridEventBusMessage>(REFRESH_GRID, true);
                        }
                    })
                    .catch(() => {
                        onSubmit('error');
                    });
            }
            ShowSuccess(t('common:actionsSuccessfulToast'));
            onSubmit(id);
        } catch {
            ShowError(t('common:errorMessage'));
            onSubmit(undefined);
        }
    };

    const onCloseHandler = async () => {
        const confirmed = await confirmLossOfChanges();
        if (!confirmed) {
            return;
        }
        onClose();
    };

    useEffect(() => {
        if (attendeesData) {
            let selectedAttendees = selectedAttendeeItems
                .filter((item) => attendeesData.some((attendee) => attendee.id === item.riskReviewAttendeeId))
                .map((item) => attendeesData.find((attendee) => attendee.id === item.riskReviewAttendeeId)!);

            const attendees = attendeesData.filter(
                (attendee) =>
                    !selectedAttendees.includes(attendee) &&
                    !pendingSelectedAttendeeIds.includes(attendee.id!) &&
                    attendee.active !== false,
            );

            if (pendingSelectedAttendeeIds.length > 0) {
                const pendingSelectedAttendees = attendeesData.filter((attendee) =>
                    pendingSelectedAttendeeIds.includes(attendee.id!),
                );
                if (pendingSelectedAttendees.length > 0) {
                    selectedAttendees = [...selectedAttendees, ...pendingSelectedAttendees];
                    setPendingSelectedAttendeeIds(
                        (prev) =>
                            prev.filter((id) => !pendingSelectedAttendees.some((attendee) => attendee.id === id))!,
                    );
                }
            }

            setAvailableAttendeeItems(mapAttendeesToAttendeesItems(attendees));
            setSelectedAttendeeItems(mapAttendeesToAttendeesItems(selectedAttendees));
            updateAttendeesDirty(selectedAttendees);
        }
    }, [attendeesData, pendingSelectedAttendeeIds]);

    const attendeesDragDropList = React.useMemo(
        () => (
            <Card title={t('ra:attendees')} data-testid="attendees-card" style={{ height: '330px' }}>
                <DragDropListsCore
                    list1Header={t('common:selectedAttendees')}
                    list2Header={t('common:availableAttendees')}
                    list1Items={selectedAttendeeItems}
                    list2Items={availableAttendeesItems}
                    idField="riskReviewAttendeeId"
                    isTwoLists
                    onChangeItemsLeft={(displayPanels) => {
                        const attendeeItems = displayPanels as AttendeeDragDropListItem[];
                        setSelectedAttendeeItems(attendeeItems);

                        const attendeesSource = hasNewAttendees()
                            ? attendeesData
                            : attendeesData || riskReviewAttendees;

                        const attendees = attendeeItems.map(
                            (attendeeItem) =>
                                attendeesSource!.find((attendee) => attendee.id === attendeeItem.riskReviewAttendeeId)!,
                        );

                        updateAttendeesDirty(attendees);
                    }}
                    onChangeItemsRight={(displayPanels) => {
                        setAvailableAttendeeItems(displayPanels as AttendeeDragDropListItem[]);
                    }}
                    renderItemLeft={(item) => {
                        return <DragDropItem {...item} toDisplayColumn />;
                    }}
                    renderItemRight={(item) => {
                        return <DragDropItem {...item} toDisplayColumn />;
                    }}
                    list1HeaderActions={
                        <Button
                            primary
                            type="button"
                            data-testid="risk-review-add-attendee-button"
                            onClick={() => showManageAttendeesDialog.setTrue()}
                        >
                            <FontAwesomeIcon icon={['far', 'plus']} /> {t('common:new')}
                        </Button>
                    }
                />
            </Card>
        ),
        [selectedAttendeeItems, availableAttendeesItems],
    );

    if (showManageAttendeesDialog.value) {
        return (
            <ManageAttendeeDialog
                studyId={studyId}
                forceActive
                onSubmit={(attendeeId) => onAttendeeDialogSubmitted(attendeeId)}
                onClose={() => showManageAttendeesDialog.setFalse()}
            />
        );
    }

    return (
        <DraggableDialog
            title={dialogTitle}
            dataTestId="manage-risk-reviews-dialog"
            initialWidth={860}
            initialHeight={dialogInitialHeight}
            onClose={onCloseHandler}
            onSubmit={() => handleSubmit(onSubmitHandler)()}
            createdUpdatedInfo={dialogCreatedUpdatedInfo}
            primaryBtnLabel={dialogPrimaryButtonLabel}
            primaryBtnDisabled={
                !isValid ||
                !isDirty ||
                isSubmitting ||
                datasetLoading ||
                (isLoadingAttendees && pendingSelectedAttendeeIds.length > 0)
            }
        >
            <form id="manage-risk-reviews" className="k-w-full k-h-full">
                <div className="k-d-grid k-grid-rows-2" style={{ gridTemplateColumns: '420px min-content 210px' }}>
                    <TextBoxController
                        control={control}
                        name="description"
                        required
                        id="risk-review-description"
                        data-testid="risk-review-description-input"
                        label={`${t('description')} *`}
                        maxLength={70}
                    />
                    <div className={cn(styles.separator, 'k-rowspan-2')} />
                    <DatePickerController
                        control={control}
                        name="startDate"
                        required
                        label={`${t('ra:startDate')} *`}
                        data-testid="risk-review-start-date-input"
                        max={endDate ?? undefined}
                        fillMode="solid"
                        disabled
                    />
                    <TextBoxController
                        control={control}
                        name="protocolVersion"
                        data-testid="risk-review-protocol-version-input"
                        label={t('ra:protocolVersion')}
                        maxLength={70}
                    />
                    <DatePickerController
                        control={control}
                        name="endDate"
                        min={startDate ?? undefined}
                        label={t('ra:endDate')}
                        data-testid="risk-review-end-date-input"
                        fillMode="solid"
                        disabled={endDateDisabled}
                    />
                </div>
                <div className="k-d-flex k-pt-md">{attendeesDragDropList}</div>
                <div className="k-d-flex k-pt-sm">
                    <TextAreaController
                        className={styles.commentsTextArea}
                        maxLength={32000}
                        control={control}
                        name="comments"
                        data-testid="risk-review-statement"
                        label={t('ra:comments')}
                    />
                </div>
            </form>
        </DraggableDialog>
    );
};

export default ManageRiskReviewDialog;
